import { RoleGroups, OrgRoleTitles } from '@copilot/common/store/models/const/enum';
import { Routes, Route } from 'react-router-dom';
import { BaseAppLayout } from './layouts/baseAppLayout';
import RouteWithPermissions from '@copilot/common/router/routeWithPermissions';
import SettingsPage from '@copilot/common/pages/settings/settingsPage';
import AdminHub from '@copilot/common/pages/adminHub';
import ImpersonationRedirect from '@copilot/common/pages/impersonate/impersonationRedirect';
import { InboxPageV2Smart } from '@copilot/common/pages/inboxV2/inboxPageV2Smart';
import MeetingsBookedPage from '@copilot/common/pages/meetingsBooked/meetingsBookedPageSmart';
import { AllCampaignsPageContainer } from '@copilot/common/pages/allCampaigns/allCampaignsPageContainer';
import OrganizationDashboard from '@copilot/common/pages/organizationDashboard/organizationDashboardPage';
import { SentMessageContainer } from '@copilot/common/pages/sent/ui/container';
import OutboxPage from '@copilot/common/pages/outbox';
import ConnectionsPage from '@copilot/common/pages/connections';
import { lazy, Suspense } from 'react';
import { AppLoader } from '../components/app-loader/appLoader';
import ErrorBoundary from '@copilot/common/components/containers/errorBoundary';
/**
 * Router for the base copilot prospecting app
 * @param roleReq - The role requirements for the router
 * @returns The base app router
 */
export function BaseAppRouter({ roleReq }: { roleReq: OrgRoleTitles[] }) {
    const CampaignDetailsPage = lazy(() => import('../pages/campaignDetails/campaignDetailsPage'));

    return (
        <>
            <BaseAppLayout>
                <Routes>
                    <Route
                        path="/"
                        element={
                            <RouteWithPermissions
                                element={<OrganizationDashboard />}
                                roleBasedComponents={{
                                    [OrgRoleTitles.Owner]: OrganizationDashboard,
                                    [OrgRoleTitles.Admin]: OrganizationDashboard,
                                    [OrgRoleTitles.Advanced]: InboxPageV2Smart,
                                    [OrgRoleTitles.User]: InboxPageV2Smart,
                                }}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/allCampaigns"
                        element={
                            <RouteWithPermissions
                                element={<AllCampaignsPageContainer />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/campaign/:campaignId?"
                        element={
                            <RouteWithPermissions
                                element={
                                    <ErrorBoundary message="Something went wrong. Please refresh the page and try again">
                                        <Suspense fallback={<AppLoader isFullScreen={false} />}>
                                            <CampaignDetailsPage />
                                        </Suspense>
                                    </ErrorBoundary>
                                }
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/inbox"
                        element={
                            <RouteWithPermissions
                                element={<InboxPageV2Smart key="inbox" />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/team-inbox"
                        element={
                            <RouteWithPermissions
                                element={<InboxPageV2Smart isTeamInboxView key="team-inbox" />}
                                allowedRoles={RoleGroups.AtLeastAdmin}
                            />
                        }
                    />
                    <Route
                        path="/meetings-booked"
                        element={
                            <RouteWithPermissions
                                element={<MeetingsBookedPage />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/my-inbox"
                        element={
                            <RouteWithPermissions
                                element={<InboxPageV2Smart key="my-inbox" />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/settings/:tab?"
                        element={
                            <RouteWithPermissions
                                element={<SettingsPage hideEmails hidePause />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/sent"
                        element={
                            <RouteWithPermissions
                                element={<SentMessageContainer />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/outbox"
                        element={
                            <RouteWithPermissions element={<OutboxPage />} allowedRoles={roleReq} />
                        }
                    />
                    <Route
                        path="/connections/:connectionId?"
                        element={
                            <RouteWithPermissions
                                element={<ConnectionsPage />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                    <Route
                        path="/adminhub/:tenantId?"
                        element={
                            <RouteWithPermissions
                                element={<AdminHub />}
                                allowedRoles={RoleGroups.SysAdmin}
                            />
                        }
                    />
                    <Route
                        path="/impersonate/:adminUserId/:impersonateeId"
                        element={
                            <RouteWithPermissions
                                element={<ImpersonationRedirect />}
                                allowedRoles={roleReq}
                            />
                        }
                    />
                </Routes>
            </BaseAppLayout>
        </>
    );
}
